<template>
  <section class="review_all_nominations px-3 pb-3">
    <strong class="review_all_nominations_title h3">All nominations in draft</strong>

    <div v-if="loadingNominations" class="text-center py-3">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <template v-else-if="groupedNominations.length > 0">
      <div class="alert alert-success custom-alert-success mt-2">
        No further action is required for your held nominations. You'll be notified, via email, when the committee finalises nominations
      </div>

      <form-alert :message="scratchAlert.message.value" :type="scratchAlert.type.value" class="mb-2" />
      <bulk-editor-tool
        :total-count="allNomIds.length"
        :selected-count="scratchSelectedIds.length"
        @toggle-all="toggleSelectAll"
      >
        <app-button
          type="button"
          variant="outline-primary"
          :block="false"
          @click="scratchConfirmPending = true"
        >
          Scratch
        </app-button>
      </bulk-editor-tool>

      <div v-for="group in groupedNominations" :key="group.event.id" class="mt-3">
        <div class="event_info mb-2 pl-2">
          <div><strong>{{ group.event.name }}</strong></div>
          <div>
            <strong>Price for one nomination - ${{ group.event.cost }}</strong>
            <strong v-if="group.event.discount_price"> (${{ group.event.discount_price }} for one with the discount applied)</strong>
          </div>
          <div><strong>Total price for all your nominations in the event - ${{ group.totalPrice }}</strong></div>
        </div>

        <form-alert
          :message="getGroupAlert(group.event.id).message.value"
          :type="getGroupAlert(group.event.id).type.value"
          class="mb-2"
        />

        <div class="cut-table table-hover table-nominations">
          <div class="cut-table__header">
            <ul class="cut-table__row row m-0">
              <li class="cut-table__cell col-md-4"><strong>Competitor name</strong></li>
              <li class="cut-table__cell col-md-4"><strong>{{ group.isNoHorse ? 'Quantity' : 'Horse name' }}</strong></li>
              <li class="cut-table__cell col-md-2"><strong>Status</strong></li>
              <li class="cut-table__cell col-md-2"><strong>Scratch</strong></li>
            </ul>
          </div>
          <div class="cut-table__body">
            <template v-for="memberGroup in group.memberGroups">
              <draggable
                :key="memberGroup.rider.id"
                tag="div"
                handle=".icon-btn--drag"
                :list="memberGroup.nominations"
                :group="{ name: 'member-' + memberGroup.rider.id, pull: false, put: false }"
                @update="onDragEnd(memberGroup.nominations, group.event.id)"
              >
                <ul
                  v-for="nom in memberGroup.nominations"
                  :key="nom.id"
                  :class="['cut-table__row', 'nominations_added_list', 'row', 'm-0', memberGroup.colorIndex % 2 === 0 ? 'nom-row--even' : 'nom-row--odd']"
                >
                  <li class="cut-table__cell member_name col-12 col-md-4">
                    <div class="d-flex align-items-center">
                      <f-checkbox
                        :id="`nom-check-${nom.id}`"
                        label=""
                        :value="scratchSelectedIds.indexOf(nom.id) !== -1"
                        class="mb-0 mr-1"
                        @input="toggleScratchSelect(nom.id)"
                      />
                      <span class="icon-btn icon-btn--drag" title="Drag to reorder" />
                      <div class="form-group mb-0 flex-grow-1">
                        <multiselect
                          :value="riderOverrides[nom.id] != null ? riderOverrides[nom.id] : riderForNom(nom)"
                          :options="membersByNom[nom.id] || []"
                          label="full_name"
                          track-by="id"
                          placeholder="Select member..."
                          select-label=""
                          deselect-label=""
                          selected-label=""
                          @open="loadMembersForNom(nom.id, nom.horse.id)"
                          @input="onRiderSelect(nom.id, $event, group.event.id)"
                        />
                      </div>
                    </div>
                  </li>
                  <li v-if="nom.horse.id != null" class="cut-table__cell horse_name col-12 col-md-4">
                    <multiselect
                      :value="horseOverrides[nom.id] != null ? horseOverrides[nom.id] : nom.horse"
                      :options="horseOptionsForNom(nom.id, nom.horse.id)"
                      label="name"
                      track-by="id"
                      placeholder="Select horse..."
                      select-label=""
                      deselect-label=""
                      selected-label=""
                      @open="loadHorsesForNom(nom.id, nom.rider.id, group.event.id)"
                      @input="onHorseSelect(nom.id, $event, group.event.id)"
                    >
                      <template #option="{ option }">
                        <span :class="{ 'text-muted': option.disabled }">
                          {{ option.name }}
                          <small v-if="option.disabled_reason" class="d-block" style="font-size: 0.75em; line-height: 1.2">{{ option.disabled_reason }}</small>
                        </span>
                      </template>
                    </multiselect>
                  </li>
                  <li v-else class="cut-table__cell horse_name quantity_cell col-12 col-md-4">
                    <input
                      type="number"
                      min="1"
                      :max="quantityMaxByNomId[nom.id]"
                      :disabled="quantityUpdatingIds.indexOf(nom.id) !== -1"
                      class="form-control quantity_input"
                      :value="quantityOverrides[nom.id] != null ? quantityOverrides[nom.id] : nom.quantity"
                      @change="onQuantityChange(nom.id, $event.target.value, group.event.id)"
                    />
                  </li>
                  <li class="cut-table__cell col-6 col-md-2 nomination_status">
                    <span class="badge ml-1 my-auto alert-info text-capitalize">{{ nom.status }}</span>
                    <span v-if="combinedGroupNumbers[nom.id] != null" class="badge badge-combined ml-1 my-auto">
                      Combined #{{ combinedGroupNumbers[nom.id] }}
                    </span>
                  </li>
                  <li class="cut-table__cell col-6 col-md-2 d-flex action_remove">
                    <div class="actions__draft _type-delete">
                      <app-button
                        variant="link"
                        :block="false"
                        class="icon-btn icon-btn--delete p-0"
                        @click="deleteConfirmPending = { nomId: nom.id, eventId: group.event.id }"
                      />
                    </div>
                  </li>
                </ul>
              </draggable>
            </template>
          </div>
        </div>
      </div>
    </template>

    <strong v-else class="d-block mt-2">No nomination is found</strong>

    <confirm-modal
      v-if="scratchConfirmPending"
      message="Are you sure you want to scratch selected nominations?"
      @confirm="handleBulkScratch"
      @cancel="scratchConfirmPending = false"
    />

    <confirm-modal
      v-if="deleteConfirmPending"
      message="Are you sure you want to delete the nomination?"
      @confirm="handleDelete"
      @cancel="deleteConfirmPending = null"
    />
  </section>
</template>

<script>
import { defineComponent, ref, computed } from '@vue/composition-api'
import Multiselect from '@/components/AppMultiselect'
import Draggable from 'vuedraggable'
import AppButton from '@/components/AppButton'
import FCheckbox from '@/components/FormFields/FCheckbox'
import FormAlert from '@/components/FormAlert'
import ConfirmModal from '@/components/ConfirmModal'
import BulkEditorTool from '@/components/BulkEditorTool'
import nominationsApi from '@/services/api/nominations'
import useFormAlert from '@/composables/useFormAlert'

export default defineComponent({
  name: 'NominationsAddList',
  components: { Multiselect, Draggable, AppButton, FCheckbox, FormAlert, ConfirmModal, BulkEditorTool },
  props: {
    userNominations: { type: Array, default: () => [] },
    loadingNominations: { type: Boolean, default: false },
    // Admin browsing another member's nominations (see NominationsAdd.vue) — lets the
    // competitor picker show that member's family instead of the admin's own. Ignored by
    // the backend for anyone who isn't an Admin.
    targetUserId: { type: Number, default: null }
  },
  setup(props, { emit }) {
    const groupAlerts = {}
    function getGroupAlert(eventId) {
      if (groupAlerts[eventId] == null) {
        groupAlerts[eventId] = useFormAlert()
      }
      return groupAlerts[eventId]
    }
    const scratchAlert = useFormAlert()

    const scratchSelectedIds = ref([])
    const scratchConfirmPending = ref(false)
    const deleteConfirmPending = ref(null)

    const membersByNom = ref({})
    const horsesByNom = ref({})
    const horseOverrides = ref({})
    const riderOverrides = ref({})
    const quantityOverrides = ref({})

    async function loadMembersForNom(nomId, horseId) {
      if (membersByNom.value[nomId] != null) return
      membersByNom.value = { ...membersByNom.value, [nomId]: [] }
      try {
        const params = { horse_id: horseId }
        if (props.targetUserId != null) params.user_id = props.targetUserId
        const res = await nominationsApi.members(params)
        membersByNom.value = { ...membersByNom.value, [nomId]: res.data.data }
      } catch (_) {
        // silent
      }
    }

    async function loadHorsesForNom(nomId, riderId, eventId) {
      if (horsesByNom.value[nomId] != null) return
      horsesByNom.value = { ...horsesByNom.value, [nomId]: [] }
      try {
        const res = await nominationsApi.memberHorses(riderId, { event_id: eventId })
        const horses = res.data.data.horses != null ? res.data.data.horses : []
        horsesByNom.value = { ...horsesByNom.value, [nomId]: horses }
      } catch (_) {
        // silent
      }
    }

    const groupedNominations = computed(() => {
      const groups = {}
      props.userNominations.forEach((nom) => {
        const key = nom.event.id
        if (groups[key] == null) {
          groups[key] = { event: nom.event, nominations: [] }
        }
        groups[key].nominations.push(nom)
      })
      return Object.values(groups).sort((a, b) => b.event.id - a.event.id).map((group, groupIndex) => {
        const memberMap = {}
        group.nominations.forEach((nom) => {
          const key = nom.rider.id
          if (memberMap[key] == null) {
            memberMap[key] = { rider: nom.rider, nominations: [] }
          }
          memberMap[key].nominations.push(nom)
        })
        const memberGroups = Object.values(memberMap).map((mg, i) => ({
          ...mg,
          colorIndex: (groupIndex + i) % 2
        }))
        const totalPrice = group.nominations.reduce((sum, nom) => sum + parseFloat(nom.effective_cost), 0)
        const isNoHorse = group.nominations.length > 0 && group.nominations[0].horse.id == null
        return { ...group, memberGroups, totalPrice, isNoHorse }
      })
    })

    // Per-member remaining Quantity quota for a no-horse event, keyed by nomination id — mirrors
    // the backend's per-person max_submissions check (this member's other quantities in the same
    // event, capped at event.max_submissions). Kept separate from groupedNominations so the
    // draggable member-group arrays stay untouched (vuedraggable mutates them in place on reorder).
    const quantityMaxByNomId = computed(() => {
      const result = {}
      groupedNominations.value.forEach((group) => {
        if (group.event.max_submissions == null) return
        group.memberGroups.forEach((mg) => {
          const memberTotal = mg.nominations.reduce((sum, n) => sum + (n.quantity || 0), 0)
          mg.nominations.forEach((n) => {
            result[n.id] = group.event.max_submissions - (memberTotal - (n.quantity || 0))
          })
        })
      })
      return result
    })

    const allNomIds = computed(() => props.userNominations.map(n => n.id))

    const combinedGroupNumbers = computed(() => {
      const map = {}
      const seen = {}
      let counter = 0
      props.userNominations.forEach((nom) => {
        if (nom.comb_nomination_id == null) return
        const pairKey = [nom.id, nom.comb_nomination_id].sort().join('-')
        if (seen[pairKey] == null) {
          counter += 1
          seen[pairKey] = counter
        }
        map[nom.id] = seen[pairKey]
      })
      return map
    })

    function extractApiError(err) {
      const data = err.response != null ? err.response.data : null
      return data != null && data.message != null ? data.message : 'A server error occurred'
    }

    function toggleScratchSelect(nomId) {
      const idx = scratchSelectedIds.value.indexOf(nomId)
      if (idx === -1) {
        scratchSelectedIds.value = [...scratchSelectedIds.value, nomId]
      } else {
        scratchSelectedIds.value = scratchSelectedIds.value.filter(id => id !== nomId)
      }
    }

    function toggleSelectAll() {
      if (scratchSelectedIds.value.length === allNomIds.value.length) {
        scratchSelectedIds.value = []
      } else {
        scratchSelectedIds.value = [...allNomIds.value]
      }
    }

    async function handleDelete() {
      if (deleteConfirmPending.value == null) return
      const { nomId, eventId } = deleteConfirmPending.value
      deleteConfirmPending.value = null
      try {
        const res = await nominationsApi.deleteNomination(nomId)
        const msg = res != null && res.data != null ? res.data.message : null
        if (msg) getGroupAlert(eventId).showSuccess(msg)
        emit('reload')
      } catch (err) {
        getGroupAlert(eventId).showError(extractApiError(err))
      }
    }

    async function handleBulkScratch() {
      scratchConfirmPending.value = false
      if (scratchSelectedIds.value.length === 0) return
      try {
        const res = await nominationsApi.bulkScratch({ nomination_ids: scratchSelectedIds.value })
        scratchSelectedIds.value = []
        const msg = res != null && res.data != null ? res.data.message : null
        if (msg) scratchAlert.showSuccess(msg)
        emit('reload')
      } catch (err) {
        scratchAlert.showError(extractApiError(err))
      }
    }

    async function handleRiderChange(nomId, memberId, eventId) {
      try {
        const res = await nominationsApi.updateNomination(nomId, { user_member_id: memberId })
        const newCache = { ...horsesByNom.value }
        delete newCache[nomId]
        horsesByNom.value = newCache
        const msg = res != null && res.data != null ? res.data.message : null
        if (msg) getGroupAlert(eventId).showSuccess(msg)
        emit('reload')
      } catch (err) {
        const reverted = { ...riderOverrides.value }
        delete reverted[nomId]
        riderOverrides.value = reverted
        getGroupAlert(eventId).showError(extractApiError(err))
      }
    }

    async function handleHorseChange(nomId, horseId, eventId) {
      try {
        const res = await nominationsApi.updateNomination(nomId, { horse_id: Number(horseId) })
        const newMemberCache = { ...membersByNom.value }
        delete newMemberCache[nomId]
        membersByNom.value = newMemberCache
        const msg = res != null && res.data != null ? res.data.message : null
        if (msg) getGroupAlert(eventId).showSuccess(msg)
        emit('reload')
      } catch (err) {
        const reverted = { ...horseOverrides.value }
        delete reverted[nomId]
        horseOverrides.value = reverted
        getGroupAlert(eventId).showError(extractApiError(err))
      }
    }

    const quantityUpdatingIds = ref([])

    async function handleQuantityChange(nomId, quantity, eventId) {
      quantityUpdatingIds.value = quantityUpdatingIds.value.concat(nomId)
      try {
        const res = await nominationsApi.updateNomination(nomId, { quantity })
        const msg = res != null && res.data != null ? res.data.message : null
        if (msg) getGroupAlert(eventId).showSuccess(msg)
        emit('reload')
      } catch (err) {
        const reverted = { ...quantityOverrides.value }
        delete reverted[nomId]
        quantityOverrides.value = reverted
        getGroupAlert(eventId).showError(extractApiError(err))
      } finally {
        quantityUpdatingIds.value = quantityUpdatingIds.value.filter(id => id !== nomId)
      }
    }

    function onQuantityChange(nomId, value, eventId) {
      let quantity = Number(value)
      if (!Number.isInteger(quantity) || quantity < 1) return
      const max = quantityMaxByNomId.value[nomId]
      if (max != null && quantity > max) quantity = max
      quantityOverrides.value = { ...quantityOverrides.value, [nomId]: quantity }
      handleQuantityChange(nomId, quantity, eventId)
    }

    function horseOptionsForNom(nomId, currentHorseId) {
      const horses = horsesByNom.value[nomId]
      if (horses == null) return []
      return horses
        .filter(h => h.id !== currentHorseId)
        .map(h => (h.disabled ? { ...h, $isDisabled: true } : h))
    }

    function onHorseSelect(nomId, horse, eventId) {
      if (horse == null || horse.disabled) return
      horseOverrides.value = { ...horseOverrides.value, [nomId]: horse }
      handleHorseChange(nomId, horse.id, eventId)
    }

    function riderForNom(nom) {
      const members = membersByNom.value[nom.id]
      if (members != null) {
        const found = members.find(m => m.id === nom.rider.id)
        if (found) return found
      }
      return { id: nom.rider.id, full_name: `${nom.rider.first_name} ${nom.rider.last_name}` }
    }

    function onRiderSelect(nomId, member, eventId) {
      if (member == null) return
      riderOverrides.value = { ...riderOverrides.value, [nomId]: member }
      handleRiderChange(nomId, member.id, eventId)
    }

    async function onDragEnd(nominations, eventId) {
      const items = nominations.map((nom, index) => ({ id: nom.id, order: index + 1 }))
      try {
        const res = await nominationsApi.reorderNominations({ items })
        const msg = res != null && res.data != null ? res.data.message : null
        if (msg) getGroupAlert(eventId).showSuccess(msg)
        emit('reload')
      } catch (err) {
        getGroupAlert(eventId).showError(extractApiError(err))
      }
    }

    return {
      scratchAlert,
      scratchSelectedIds,
      scratchConfirmPending,
      deleteConfirmPending,
      membersByNom,
      loadMembersForNom,
      horsesByNom,
      loadHorsesForNom,
      groupedNominations,
      allNomIds,
      combinedGroupNumbers,
      getGroupAlert,
      toggleScratchSelect,
      toggleSelectAll,
      handleDelete,
      handleBulkScratch,
      handleRiderChange,
      handleHorseChange,
      onDragEnd,
      horseOverrides,
      riderOverrides,
      quantityOverrides,
      quantityMaxByNomId,
      quantityUpdatingIds,
      horseOptionsForNom,
      onHorseSelect,
      riderForNom,
      onRiderSelect,
      onQuantityChange
    }
  }
})
</script>

<style scoped>
.badge-combined {
  background-color: var(--primary);
  color: var(--white);
}

.nom-row--even {
  background-color: var(--white);
}

.nom-row--odd {
  background-color: var(--gray-75);
}

.review_all_nominations .icon-btn--drag {
  display: block;
  width: 40px;
  flex-shrink: 0;
}

.review_all_nominations >>> .form-control {
  margin-top: 0;
}

.quantity_cell {
  display: flex;
  align-items: center;
}

.quantity_input {
  max-width: 100px;
}

</style>



// WEBPACK FOOTER //
// src/views/Nominations/components/NominationsAddList.vue